import { useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../store";
import { useGetABOEnrichmentDataMutation } from "../../../services/AboEnrichment/AboEnrichmentAPI";
import { addFetchedAbos, markAbosFailed } from "../../../reducers/aboEnrichment";
import { calculateBatchToFetch } from "../utils/batchCalculation";
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../../store";
import type { AboKpiData } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";

export type FetchState = "unfetched" | "fetching" | "fetched" | "failed" | "skipped";

interface UseLazyAboEnrichmentProps {
  reportId: string;
  period: string;
  kpis: string[];
  locale: string;
  isoCountryCode: string;
  batchSize?: number;
  skipEnrichment?: boolean;
  transformEnrichedData?: (data: AboKpiData[]) => AboKpiData[];
  // Set of aff-abos in the report that are customers (business nature 3/4/17).
  // For these, the API sources PV from customer orders rather than los-lite.
  customerAffAbos?: Set<string>;
}

const defaultPeriodData = {
  affAbosFetched: [] as string[],
  affAbosFailed: [] as string[],
  enrichedData: [] as never[],
};

const selectEnrichmentData = createSelector(
  [
    (state: RootState) => state.aboEnrichment,
    (_: RootState, reportId: string) => reportId,
    (_: RootState, __: string, period: string) => period,
  ],
  (aboEnrichment, reportId, period) => aboEnrichment[reportId]?.[period] || defaultPeriodData,
);

export const useLazyAboEnrichment = ({
  reportId,
  period,
  kpis,
  locale,
  isoCountryCode,
  batchSize = 10,
  skipEnrichment = false,
  transformEnrichedData,
  customerAffAbos,
}: UseLazyAboEnrichmentProps) => {
  const dispatch = useAppDispatch();
  const [fetchEnrichmentData] = useGetABOEnrichmentDataMutation();
  const fetchingRef = useRef<Set<string>>(new Set());

  const enrichmentData = useAppSelector((state) => selectEnrichmentData(state, reportId, period));

  const getFetchState = useCallback(
    (affAbo: string): FetchState => {
      if (skipEnrichment === true) return "skipped";
      if (fetchingRef.current.has(affAbo)) return "fetching";
      if (enrichmentData.affAbosFailed?.includes(affAbo)) return "failed";
      if (enrichmentData.affAbosFetched.includes(affAbo)) return "fetched";
      return "unfetched";
    },
    [enrichmentData.affAbosFetched, enrichmentData.affAbosFailed, skipEnrichment],
  );

  const fetchAboData = useCallback(
    async (visibleAffAboIds: string[], allAffAboIds?: string[]) => {
      if (skipEnrichment) return;
      // If allAffAboIds is provided, use calculateBatchToFetch for smarter batching
      // Otherwise, fall back to simple filtering of visible IDs
      const idsToProcess = allAffAboIds
        ? calculateBatchToFetch(visibleAffAboIds, allAffAboIds, getFetchState, batchSize)
        : visibleAffAboIds.filter((id) => getFetchState(id) === "unfetched");

      if (idsToProcess.length === 0) return;

      // When using calculateBatchToFetch, it already returns at most batchSize IDs
      // When not using it, we need to batch the unfetched IDs
      const batches = allAffAboIds
        ? [idsToProcess] // Already batched by calculateBatchToFetch
        : [];

      if (!allAffAboIds) {
        for (let i = 0; i < idsToProcess.length; i += batchSize) {
          batches.push(idsToProcess.slice(i, i + batchSize));
        }
      }

      for (const batch of batches) {
        batch.forEach((id) => fetchingRef.current.add(id));

        try {
          const customers = customerAffAbos ? batch.filter((id) => customerAffAbos.has(id)) : undefined;
          const response = await fetchEnrichmentData({
            affAbos: batch,
            bonusPeriod: period,
            locale,
            isoCountryCode,
            kpis,
            customers,
          }).unwrap();

          const enrichedData = transformEnrichedData ? transformEnrichedData(response.aboKpiData) : response.aboKpiData;

          dispatch(
            addFetchedAbos({
              reportId,
              period,
              affAbos: batch,
              enrichedData,
            }),
          );
        } catch {
          dispatch(
            markAbosFailed({
              reportId,
              period,
              affAbos: batch,
            }),
          );
        } finally {
          batch.forEach((id) => fetchingRef.current.delete(id));
        }
      }
    },
    [
      reportId,
      period,
      kpis,
      locale,
      isoCountryCode,
      batchSize,
      getFetchState,
      dispatch,
      fetchEnrichmentData,
      transformEnrichedData,
      customerAffAbos,
    ],
  );

  const getEnrichedAbo = useCallback(
    (affAbo: string) => {
      return enrichmentData.enrichedData.find((data) => `${data.aff}-${data.abo}` === affAbo);
    },
    [enrichmentData.enrichedData],
  );

  return { fetchAboData, getEnrichedAbo, getFetchState, enrichmentData };
};
